import { create } from 'zustand';
import {
  categoryNameKey,
  countKey,
  filterByLangValue,
  filterByRange,
  sortByCost,
  sortByLangValue,
} from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  DishCategoryType,
  FetchDishCategoriesRequest,
  deleteCategory,
  fetchCategories,
  postCategory,
  putCategoriesOrder,
  putCategory,
} from './api/dish-category-api';
import { useAlertStore } from './use-alert-store';
import {
  createSectionCategory,
  updateSectionCategory,
  deleteSectionCategory,
  sortSectionCategories,
} from '../pages/manager/body/menu/menu-informer/menu-informer-category-helpers';

interface Store extends Partial<PaginationType> {
  isLoading?: boolean;
  setCategoryListOrder: (order: number[], sectionId?: number) => void;
  postCategory: (
    category: Pick<DishCategoryType, 'name' | 'section_id'>,
  ) => Promise<DishCategoryType | undefined>;
  putCategory: (category: DishCategoryType) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  list?: DishCategoryType[];
  fetchCategories: (params: FetchDishCategoriesRequest) => Promise<void>;
}

export const useDishCategoryStore = create<Store>((set) => ({
  postCategory: async (category) => {
    set({ isLoading: true });
    let resp: DishCategoryType | undefined;
    try {
      resp = await postCategory(category);
      createSectionCategory(resp);
      useAlertStore.getState().add({ label: 'Категория успешно добавлена', variant: 'success' });
    } catch (e) {
      resp = undefined;
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по категории', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
    return resp;
  },
  putCategory: async (category) => {
    set({ isLoading: true });
    try {
      const resp = await putCategory(category);
      updateSectionCategory(resp);
      useAlertStore.getState().add({ label: 'Категория успешно обновлена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по категории', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteCategory: async (id) => {
    set({ isLoading: true });
    try {
      await deleteCategory(id);
      deleteSectionCategory(id);
      useAlertStore.getState().add({ label: 'Категория успешно удалена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить категорию', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },

  setCategoryListOrder: async (order, sectionId) => {
    set({ isLoading: true });
    try {
      await putCategoriesOrder(order);
      sortSectionCategories(sectionId, order);
      useAlertStore
        .getState()
        .add({ label: 'Порядок категорий успешно сохранен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить порядок категорий', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchCategories: async (params) => {
    set({ isLoading: true });
    try {
      const { categories } = await fetchCategories(params);
      let list = [...categories];

      if (params.section) {
        list = list.filter((item) => params.section?.includes(String(item.section_id)));
      }

      if (params.name) {
        list = list.filter((item) => filterByLangValue(item.name, params.name));
      }

      if (params.count) {
        list = list.filter((item) => filterByRange(item.dishes_count, params.count));
      }

      if (params.field === countKey) {
        list = list.sort((a, b) =>
          sortByCost(a.dishes_count, b.dishes_count, params.dir as SortType),
        );
      }

      if (params.field === 'section') {
        list = list.sort((a, b) => sortByCost(a.section_id, b.section_id, params.dir as SortType));
      }

      if (params.field === categoryNameKey) {
        list = list.sort((a, b) => sortByLangValue(a.name, b.name, params.dir as SortType));
      }

      set({ list: list });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по категориям', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
